import React, { Component } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { OrganizationService, AccountService } from "@services";
import utils from "@utils";
import accountActions from "@redux/account";
import appActions from "@redux/app";
import { EditImageModal, Loader, Image } from "../../../components";

class AgencyInfo extends Component {
	constructor(props) {
		super(props);

		const { user } = props;

		this.state = {
			Name: user.Organization.Name,
			nameError: false, // true = has error
			loadingSubmit: false,
			organizationLogo: undefined,
			loadingLogo: false,
			showSubmit: false,
			validImageSelected: true,
			showEditImageModal: false,
			imageToEdit: "",
		};
	}

	/** Gathers and saves all changes made to the agency information section */
	onSubmit = () => {
		const { lang } = global;
		const { Name } = this.state;
		const { receiveAgency, updateUser, user } = this.props;

		// Name validation
		if (Name === "") {
			this.setState({ nameError: true });
			return;
		}

		this.setState({ loadingSubmit: true });

		OrganizationService.saveAgency({
			id: user.Organization.Id,
			data: { ...user.Organization, Name },
		})
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: lang.t("Agency Settings updated successfully"),
				});

				this.setState({ loadingSubmit: false, showSubmit: false });

				// Update organization name in the redux store
				receiveAgency(data);
				updateUser("Organization.Name", Name);
			})
			.catch(({ response }) => {
				this.setState({ loadingSubmit: false });

				utils.notify({
					type: "error",
					message: response.data.Message ?? lang.t("Error updating agency settings"),
				});
			});
	};

	/**
	 * Calls the API which handles uploads of a selected image
	 * @param {String} imageToSend The image blob that will be uploaded
	 */
	updateImage = imageToSend => {
		const { lang } = global;
		const { receiveAgency, setProfilePic } = this.props;

		this.setState({ loadingLogo: true });

		OrganizationService.uploadImage(imageToSend)
			.then(() => AccountService.me())
			.then(({ data }) => {
				utils.notify({
					type: "success",
					message: lang.t("Agency Photo updated successfully"),
				});

				this.setState({
					organizationLogo: data.Organization.OrganizationLogo,
					loadingLogo: false,
				});

				// Update organization logo in the redux store
				receiveAgency(data.Organization);
				setProfilePic(data.Organization.OrganizationLogo);
			})
			.catch(({ response }) => {
				this.setState({ loadingLogo: false });

				utils.notify({
					type: "error",
					message: response.data.Message ?? lang.t("Error Uploading Agency Photo"),
				});
			});
	};

	/**
	 * Opens the modal which will handle the uploading of images
	 * @param {Event} event The input element which handles the file to be uploaded
	 */
	openImageSelector = event => {
		const { target } = event;

		if (!target.files || !target.files[0]) return;
		const [{ type }] = target.files;
		this.setState({ validImageSelected: type.includes("image") }, () => {
			if (type.includes("image")) {
				this.setState({ loadingLogo: true });

				const reader = new FileReader();
				reader.readAsDataURL(target.files[0]);

				reader.onload = e => {
					this.openEditImageModal(e.target.result);
					target.value = "";
				};
			}
		});
	};

	openEditImageModal = imageToEdit => this.setState({ showEditImageModal: true, imageToEdit });

	closeEditImageModal = () =>
		this.setState({ showEditImageModal: false, imageToEdit: "", loadingLogo: false });

	onCancel = () => {
		const { user } = this.props;
		this.setState({ Name: user.Organization.Name, nameError: false, showSubmit: false });
	};

	onNameChange = event => {
		const value = event?.target?.value ?? "";
		this.setState({ Name: value, showSubmit: true });
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const {
			Name,
			nameError,
			loadingSubmit,
			organizationLogo,
			loadingLogo,
			showSubmit,
			validImageSelected,
			showEditImageModal,
			imageToEdit,
		} = this.state;

		return (
			<div className="my-account-panel">
				<div className="my-account-panel-header">
					<h4 className="title">{lang.t("Agency Information")}</h4>
					<h5 className="subtitle">{lang.t("Update Agency Name and Logo")}</h5>
				</div>
				<div className="my-account-panel-body">
					<div className="form-group image-uploader">
						<label htmlFor="upload-client-image">
							<Image
								src={organizationLogo ?? user.Organization.OrganizationLogo ?? ""}
								fallbackSrc="avatar"
							/>
							<div className="blur">{lang.t("Update Image")}</div>
							<Loader
								active={loadingLogo}
								size="small"
							/>
						</label>
						<input
							id="upload-client-image"
							type="file"
							accept=".jpeg,.jpg,.png,.tif,.tiff"
							className="hidden"
							onChange={this.openImageSelector}
						/>
					</div>
					<If condition={!validImageSelected}>
						<div className="row form-group">
							<div className="col-sm-12 text-danger text-center">
								{lang.t(
									"Please ensure the selected image has one of the following file extensions:",
								)}
								<br />
								{lang.t("jpeg, jpg, png, tif, tiff")}
							</div>
						</div>
					</If>
					<div className={`row form-group ${nameError ? "has-error" : ""}`}>
						<div className="col-xs-12 col-sm-4">
							<label htmlFor="AgencyName">{`${lang.t("Agency Name")}:`}</label>
						</div>
						<div className="col-xs-12 col-sm-7">
							<input
								type="text"
								className="form-control"
								id="AgencyName"
								value={Name}
								onChange={this.onNameChange}
							/>
						</div>
					</div>

					<div className={`my-account-submit-buttons ${showSubmit ? "" : "not-visible"}`}>
						<button
							type="button"
							id="my-account-agency-info-save-btn"
							className="btn btn-primary"
							disabled={loadingSubmit}
							onClick={this.onSubmit}
						>
							{loadingSubmit ? lang.t("Loading") : lang.t("Update")}
						</button>
						<button
							type="button"
							id="my-account-agency-info-cancel-btn"
							className="btn btn-link"
							disabled={loadingSubmit}
							onClick={this.onCancel}
						>
							{lang.t("Cancel")}
						</button>
					</div>
				</div>
				<EditImageModal
					show={showEditImageModal}
					closeModal={this.closeEditImageModal}
					successCallback={this.updateImage}
					imageToEdit={imageToEdit}
				/>
			</div>
		);
	}
}

const mapDispatchToProps = dispatch => ({
	receiveAgency: bindActionCreators(accountActions, dispatch).receiveAgency,
	setProfilePic: bindActionCreators(accountActions, dispatch).setProfilePic,
	updateUser: bindActionCreators(appActions, dispatch).updateUser,
});

AgencyInfo.propTypes = {
	user: PropTypes.object,
	receiveAgency: PropTypes.func.isRequired,
	setProfilePic: PropTypes.func.isRequired,
	updateUser: PropTypes.func.isRequired,
};

AgencyInfo.defaultProps = {
	user: {},
};

export default connect(null, mapDispatchToProps)(AgencyInfo);
